import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useDay } from '../hooks/useToday';
import { useOpenPuzzles } from '../hooks/useOpenPuzzles';
import { useSwipe } from '../hooks/useSwipe';
import { EntryForm } from '../components/EntryForm';
import { EntryView } from '../components/EntryView';
import { HintsInline } from '../components/HintsInline';
import { OpenPuzzleNav } from '../components/OpenPuzzleNav';
import { PlayInline } from '../components/PlayInline';
import { PlayerStatus } from '../components/PlayerStatus';
import { ShareButton } from '../components/ShareButton';
import { SolutionInline } from '../components/SolutionInline';
import { puzzleLabel } from '../lib/puzzleLabel';
import type { EntryDto, Puzzle, Rank, SubmitEntryResponse } from '../api/types';

export function TodayPage() {
  const { player, logout } = useAuth();
  const today = useMemo(() => isoToday(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  // Initial-Datum aus ?date=YYYY-MM-DD (Deep-Link aus dem Share-Text).
  // Heute ist Default; ungültige Werte werden ignoriert.
  const initialDate = useMemo<string>(() => {
    const raw = searchParams.get('date');
    if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    return today;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [currentDate, setCurrentDate] = useState<string>(initialDate);

  // URL bei jedem Tag-Wechsel synchronisieren — heute → ohne Param
  // (saubere "/"-URL), sonst ?date=…
  useEffect(() => {
    if (currentDate === today) {
      if (searchParams.has('date')) {
        const next = new URLSearchParams(searchParams);
        next.delete('date');
        setSearchParams(next, { replace: true });
      }
    } else {
      if (searchParams.get('date') !== currentDate) {
        const next = new URLSearchParams(searchParams);
        next.set('date', currentDate);
        setSearchParams(next, { replace: true });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, today]);

  const { puzzle, myEntry, revealed, reveal, allEntries, loading, error, refresh } = useDay(
    currentDate,
    player?.id ?? null,
  );
  // Tage ohne eigenen Eintrag — für die Sprung-Leiste unter dem Datum.
  const { dates: openDates, error: openError, refresh: refreshOpen } = useOpenPuzzles(today);
  // Namen der Spieler, die noch nicht getippt haben — für den „Fehlt
  // noch"-Hinweis im Vorab-Share. Leer, sobald alle eingetragen haben.
  const missingPlayers = (reveal?.player_status ?? [])
    .filter((p) => !p.has_entry)
    .map((p) => p.name);
  const [justSubmitted, setJustSubmitted] = useState(false);
  // Wenn der eigene Submit den Auto-Reveal ausgelöst hat (man war der
  // letzte), öffnet sich nach dem Refresh ein Popup mit dem WhatsApp-
  // Share. Erst sichtbar, sobald `revealed` und `allEntries` aus dem
  // Refresh wirklich geladen sind — sonst hätte der Share-Text noch
  // den unvollständigen Stand vor Reveal.
  const [autoRevealPopup, setAutoRevealPopup] = useState(false);

  const isToday = currentDate === today;
  const canGoForward = currentDate < today;

  const goPrev = useCallback((): void => {
    setCurrentDate((d) => addDays(d, -1));
    setJustSubmitted(false);
    setAutoRevealPopup(false);
  }, []);
  const goNext = useCallback((): void => {
    setCurrentDate((d) => (d < today ? addDays(d, +1) : d));
    setJustSubmitted(false);
    setAutoRevealPopup(false);
  }, [today]);
  const jumpTo = useCallback((date: string): void => {
    setCurrentDate(date);
    setJustSubmitted(false);
    setAutoRevealPopup(false);
  }, []);
  const goToday = (): void => jumpTo(today);

  const handleSubmitted = useCallback((data: SubmitEntryResponse): void => {
    setJustSubmitted(true);
    if (data.auto_revealed) setAutoRevealPopup(true);
    void refresh();
    void refreshOpen();
  }, [refresh, refreshOpen]);

  // Swipe links → einen Tag vor; Swipe rechts → einen Tag zurück.
  // Touchscreen-Geste, fühlt sich auf dem Handy natürlich an.
  useSwipe({
    onSwipeLeft: canGoForward ? goNext : undefined,
    onSwipeRight: goPrev,
  });

  return (
    <main className="today">
      <header className="today__header">
        <DateHeader
          date={currentDate}
          puzzleNumber={puzzle?.puzzle_number ?? null}
          puzzleTitle={puzzle?.title ?? null}
          isToday={isToday}
          canGoForward={canGoForward}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
        />
        <div className="today__actions">
          <Link to="/stats" className="button button--ghost">Stats</Link>
          <Link to="/change-password" className="button button--ghost">Passwort</Link>
          <button type="button" className="button button--ghost" onClick={logout}>
            Logout
          </button>
        </div>
      </header>

      <OpenPuzzleNav dates={openDates} error={openError} currentDate={currentDate} onJump={jumpTo} />

      {isToday && <p className="today__greeting">Hallo, {player?.name}.</p>}

      {loading && <p className="today__status">Lade …</p>}

      {!puzzle && !loading && error && isToday && (
        <section className="card">
          <h2 className="card__title">Heute ist noch keins eingetragen</h2>
          <p className="card__text">
            Spiel auf <a href="https://www.pastpuzzle.de" target="_blank" rel="noopener noreferrer">pastpuzzle.de</a>,
            kopier den Share-Text und füg ihn unten ein. Wenn die
            Rätsel-Nummer noch unbekannt ist, lege ich sie automatisch an.
          </p>
          <EntryForm
            defaultPuzzleDate={currentDate}
            entryDate={currentDate}
            onSubmitted={handleSubmitted}
          />
        </section>
      )}

      {!puzzle && !loading && error && !isToday && (
        <section className="card">
          <h2 className="card__title">Kein Rätsel an diesem Tag</h2>
          <p className="card__text">
            An diesem Datum wurde nichts angelegt. Wenn du deinen
            Share-Text von damals noch hast, kannst du ihn nachträglich
            einspielen — sonst weiter zurück springen oder auf „Heute".
          </p>
          <EntryForm
            defaultPuzzleDate={currentDate}
            entryDate={currentDate}
            onSubmitted={handleSubmitted}
          />
        </section>
      )}

      {puzzle && !loading && (
        <>
          {reveal && <PlayerStatus status={reveal} />}

          {!myEntry && (
            // key: beim Tag-Wechsel neu aufsetzen, sonst bliebe der
            // gewählte Eingabe-Modus des Vortags stehen — auch wenn der
            // neue Tag gar nicht spielbar ist.
            <EntrySection
              key={puzzle.id}
              isToday={isToday}
              puzzleNumber={puzzle.puzzle_number}
              puzzleDate={puzzle.puzzle_date}
              currentDate={currentDate}
              playable={puzzle.playable}
              onSubmitted={handleSubmitted}
            />
          )}

          {myEntry && (() => {
            // Sobald der Spieler getippt hat, kommen alle bisher
            // eingegangenen Einträge vom Server — bereits mit Rang. Sie
            // werden ab sofort nach den Wertungsregeln sortiert, auch
            // bevor alle getippt haben (Zwischenstand). Ränge werden erst
            // ab ≥2 Einträgen gezeigt (kein verfrühtes 🥇 als Einziger).
            const list = allEntries.length > 0
              ? [...allEntries].sort(byRank)
              : [myEntry];
            const showRank = revealed || allEntries.length > 1;
            const title = revealed
              ? 'Ergebnisse'
              : (allEntries.length > 1 ? 'Zwischenstand' : 'Dein Eintrag');

            return (
              <section className="card today__entry-section">
                <h2 className="card__title">{title}</h2>
                {justSubmitted && !revealed && (
                  <p className="today__just-submitted">
                    ✔️ Eingetragen. Wartet auf die anderen.
                  </p>
                )}
                <div className="entry-list">
                  {list.map((e) => (
                    <EntryView
                      key={e.id}
                      entry={e}
                      puzzle={puzzle}
                      showRank={showRank}
                      isMe={e.player_id === player?.id}
                      defaultOpen={!revealed && e.player_id === player?.id}
                    />
                  ))}
                </div>
              </section>
            );
          })()}

          {myEntry && (
            <section className="card">
              <h2 className="card__title">Mein Ergebnis teilen</h2>
              <p className="card__text">
                Originalformat von pastpuzzle.de — zum Teilen mit Leuten
                außerhalb unserer Gruppe.
              </p>
              <ShareButton kind="own" puzzle={puzzle} entry={myEntry} />
            </section>
          )}

          {puzzle.hints_visible && (
            <section className="card">
              <h2 className="card__title">Hinweise</h2>
              {puzzle.also_played_dates.length > 0 && (
                <p className="hints__duplicate-note">
                  🔁 Pastpuzzle {puzzleLabel(puzzle)} kam schon mal vor:{' '}
                  {puzzle.also_played_dates.map(formatDate).join(', ')}
                </p>
              )}
              <HintsInline puzzle={puzzle} onSaved={() => void refresh()} />
              <SolutionInline puzzle={puzzle} onSaved={() => void refresh()} />
            </section>
          )}

          {allEntries.length > 0 && (
            <section className="card">
              <h2 className="card__title">
                {revealed ? 'In WhatsApp teilen' : 'Zwischenstand teilen'}
              </h2>
              <p className="card__text">
                {revealed
                  ? 'Erzeugt einen Share-Text mit Rangliste und Versuchen.'
                  : 'Teilt den aktuellen Stand — wer noch nicht getippt hat, wird im Text vermerkt.'}
              </p>
              <ShareButton puzzle={puzzle} entries={allEntries} missingPlayers={missingPlayers} />
            </section>
          )}
        </>
      )}

      {autoRevealPopup && puzzle && revealed && allEntries.length > 0 && (
        <AutoRevealSharePopup
          puzzle={puzzle}
          entries={allEntries}
          myEntry={myEntry}
          onClose={() => setAutoRevealPopup(false)}
        />
      )}
    </main>
  );
}

interface AutoRevealSharePopupProps {
  puzzle: Puzzle;
  entries: EntryDto[];
  myEntry: EntryDto | null;
  onClose(): void;
}

function AutoRevealSharePopup({ puzzle, entries, myEntry, onClose }: AutoRevealSharePopupProps) {
  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="auto-reveal-title">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel modal__panel--scroll">
        <h2 id="auto-reveal-title" className="modal__title">
          🎉 Alle haben getippt!
        </h2>
        <p className="modal__text">
          Du warst der Letzte — willst du die Runde direkt in WhatsApp teilen?
        </p>
        <h3 className="modal__subtitle">Team-Ergebnis</h3>
        <ShareButton puzzle={puzzle} entries={entries} />
        {myEntry && (
          <>
            <h3 className="modal__subtitle">Mein Ergebnis (Originalformat)</h3>
            <ShareButton kind="own" puzzle={puzzle} entry={myEntry} />
          </>
        )}
        <div className="modal__buttons">
          <button type="button" className="button button--ghost" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </div>
  );
}

interface EntrySectionProps {
  isToday: boolean;
  puzzleNumber: number;
  puzzleDate: string;
  currentDate: string;
  /** Lösung + alle 4 Hinweise liegen vor — nur dann ist der Tipp-Modus
   *  spielbar. */
  playable: boolean;
  onSubmitted(response: SubmitEntryResponse): void;
}

/**
 * Eingabe-Sektion mit Toggle zwischen "Hier tippen" (in-App spielen)
 * und "Ergebnis eintragen" (Share-Text einfügen, klassischer Flow).
 *
 * "Hier tippen" steht an erster Stelle, ist aber NICHT vorausgewählt.
 * Beides ist Absicht: das Direktspielen soll sofort ins Auge fallen,
 * gleichzeitig muss der klassische Weg ohne Umweg funktionieren — wer
 * auf pastpuzzle.de gespielt hat, will seinen Share-Text einfügen und
 * nicht erst einen Tab suchen.
 *
 * Fehlen Lösung oder Hinweise (Import ausgefallen, alter Tag ohne
 * gepflegte Daten), ist der Tipp-Tab zusätzlich deaktiviert — so läuft
 * niemand in einen Fehler, den die App vorher schon kennt.
 */
function EntrySection({
  isToday,
  puzzleNumber,
  puzzleDate,
  currentDate,
  playable,
  onSubmitted,
}: EntrySectionProps) {
  // Default bleibt bewusst das Eintragen: wer auf pastpuzzle.de gespielt
  // hat, soll sein Ergebnis ohne Umweg einfügen können. Zum Direktspielen
  // führt der Button darüber — sichtbar an erster Stelle, aber ein Tap
  // entfernt statt vorausgewählt.
  const [mode, setMode] = useState<'paste' | 'play'>('paste');
  const title = isToday ? 'Dein Ergebnis eintragen' : 'Nachträglich eintragen';
  const pasteHint = isToday
    ? 'Kopier den Share-Text aus der Pastpuzzle-App und füg ihn unten ein. Die Hinweise siehst du erst, nachdem du eingetragen hast — so spoilst du dich nicht selbst.'
    : `Du hast für diesen Tag noch keinen Eintrag. Wenn du den Share-Text noch hast, füg ihn hier ein — er wird mit dem Datum ${formatDate(puzzleDate)} gespeichert.`;
  const playHint = 'Rate die Jahreszahl direkt hier — die Hinweise werden nacheinander aufgedeckt, dein Ergebnis wird automatisch eingetragen.';

  return (
    <section className="card">
      <h2 className="card__title">{title}</h2>
      <div className="entry-mode-toggle" role="tablist" aria-label="Eingabe-Modus">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'play'}
          disabled={!playable}
          title={playable ? undefined : 'Hinweise und Lösung liegen für diesen Tag noch nicht vor'}
          className={`entry-mode-toggle__btn${mode === 'play' ? ' entry-mode-toggle__btn--active' : ''}`}
          onClick={() => setMode('play')}
        >
          Hier tippen
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'paste'}
          className={`entry-mode-toggle__btn${mode === 'paste' ? ' entry-mode-toggle__btn--active' : ''}`}
          onClick={() => setMode('paste')}
        >
          Ergebnis eintragen
        </button>
      </div>
      {mode === 'play' ? (
        <>
          <p className="card__text">{playHint}</p>
          <PlayInline puzzleNumber={puzzleNumber} puzzleDate={currentDate} onSubmitted={onSubmitted} />
        </>
      ) : (
        <>
          {!playable && (
            <p className="card__text entry-mode-toggle__note">
              Für diesen Tag fehlen noch Hinweise oder Lösung — deshalb
              geht hier nur der Share-Text.
            </p>
          )}
          <p className="card__text">{pasteHint}</p>
          <EntryForm
            expectedPuzzleNumber={puzzleNumber}
            defaultPuzzleDate={currentDate}
            entryDate={currentDate}
            onSubmitted={onSubmitted}
          />
        </>
      )}
    </section>
  );
}

interface DateHeaderProps {
  date: string;
  puzzleNumber: number | null;
  /** Bei Sonder-Rätseln wird der Titel statt der Nummer angezeigt. */
  puzzleTitle: string | null;
  isToday: boolean;
  canGoForward: boolean;
  onPrev(): void;
  onNext(): void;
  onToday(): void;
}

function DateHeader({ date, puzzleNumber, puzzleTitle, isToday, canGoForward, onPrev, onNext, onToday }: DateHeaderProps) {
  const d = new Date(`${date}T12:00:00Z`);
  return (
    <div className="today__date-header">
      <div className="today__date-row">
        <button
          type="button"
          className="day-nav"
          onClick={onPrev}
          aria-label="Tag zurück"
        >
          ‹
        </button>
        <div className="today__date-block">
          <span className="today__weekday">
            {d.toLocaleDateString('de-DE', { weekday: 'long' })}
          </span>
          <span className="today__date-big">
            {d.toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            })}
          </span>
        </div>
        <button
          type="button"
          className="day-nav"
          onClick={onNext}
          disabled={!canGoForward}
          aria-label="Tag vor"
        >
          ›
        </button>
      </div>
      <div className="today__date-meta">
        {!isToday && (
          <button type="button" className="today__today-link" onClick={onToday}>
            zurück zu heute
          </button>
        )}
        {puzzleNumber != null && (
          <span className="today__puzzle-num">
            Pastpuzzle{' '}
            {puzzleTitle ? puzzleTitle : `#${puzzleNumber}`}
          </span>
        )}
      </div>
    </div>
  );
}

const DNF_SORT = Number.MAX_SAFE_INTEGER;

function rankSortKey(rank: Rank | null): number {
  if (rank == null || rank === 'DNF') return DNF_SORT;
  return rank;
}

function byRank(a: EntryDto, b: EntryDto): number {
  return rankSortKey(a.rank) - rankSortKey(b.rank);
}

function formatDate(iso: string): string {
  const d = new Date(iso.length > 10 ? iso : `${iso}T12:00:00Z`);
  return d.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function isoToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(iso: string, delta: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
